import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ExternalLink, Loader2 } from 'lucide-react';

import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { FC_TRANSFER_DISPLAY_FALLBACK } from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';
import { Button } from 'shadcn/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { PaginationControls, ResultsCounter } from 'shared/components/pagination';

/**
 * @param {unknown} value
 * @returns {string | number}
 */
function displayValue(value) {
    if (value === undefined || value === null || value === '') {
        return FC_TRANSFER_DISPLAY_FALLBACK;
    }
    return value;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string}
 */
function getDiscrepancySummary(row) {
    const discrepancies = Array.isArray(row.discrepancies) ? row.discrepancies : [];
    if (!discrepancies.length) {
        return FC_TRANSFER_DISPLAY_FALLBACK;
    }
    return discrepancies
        .map((d) => {
            const type = d?.discrepancyType ?? 'discrepancy';
            const received = d?.receivedTotalUnits;
            if (received === undefined || received === null || received === '') {
                return String(type);
            }
            return `${type} (received ${received})`;
        })
        .join('; ');
}

const renderActionsCell = ({ row, table }) => {
    const onViewTransfer = table.options.meta?.onViewTransfer;
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="View FC transfer"
            onClick={() => onViewTransfer?.(row.original)}
        >
            <ExternalLink className="h-4 w-4" />
        </Button>
    );
};

const MemoizedRow = React.memo(({ row }) => (
    <TableRow>
        {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

/**
 * Line-item table for merch all-FC transfers (`GET …/fc-transfers/items`).
 * Server sort is fixed (transferItemId desc) — columns are not client-sortable.
 *
 * @param {Object} props
 */
export function AllFCTransferItemsTable({
    currentPage,
    data = [],
    emptyMessage = 'No FC transfer items found.',
    hasNextPage = false,
    hasPrevious = false,
    isFetching = false,
    isLoading = false,
    onNext,
    onPageSizeChange,
    onPrevious,
    onViewTransfer,
    pageSize,
    pageSizeOptions,
    totalCount: totalCountProp,
    totalPages,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(
        () => [
            {
                id: 'transferId',
                header: 'Transfer ID',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.transferId),
            },
            {
                id: 'sourceFacilityName',
                header: 'Source FC',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.sourceFacilityName),
            },
            {
                id: 'destinationFacilityName',
                header: 'Destination FC',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.destinationFacilityName),
            },
            {
                id: 'companySourceDisplayName',
                header: 'Company Source',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.companySourceDisplayName),
            },
            {
                id: 'estimatedDepartureDate',
                header: 'Estimated Departure',
                enableSorting: false,
                cell: ({ row }) =>
                    formatPurchaseOrderDate(row.original.estimatedDepartureDate, FC_TRANSFER_DISPLAY_FALLBACK),
            },
            {
                id: 'receiptStartedAt',
                header: 'Receipt Started',
                enableSorting: false,
                cell: ({ row }) => formatPurchaseOrderDate(row.original.receiptStartedAt, FC_TRANSFER_DISPLAY_FALLBACK),
            },
            {
                id: 'completedAt',
                header: 'Completed At',
                enableSorting: false,
                cell: ({ row }) => formatPurchaseOrderDate(row.original.completedAt, FC_TRANSFER_DISPLAY_FALLBACK),
            },
            {
                id: 'transferItemStatus',
                header: 'Item Status',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.transferItemStatus),
            },
            {
                id: 'externalSku',
                header: 'External SKU',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.externalSku),
            },
            {
                id: 'sourceLotId',
                header: 'Source Lot ID',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.sourceLotId),
            },
            {
                id: 'itemName',
                header: 'Item Name',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.itemName),
            },
            {
                id: 'expectedTotalUnits',
                header: 'Expected Total Units',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.expectedTotalUnits),
            },
            {
                id: 'receivedTotalUnits',
                header: 'Received Total Units',
                enableSorting: false,
                cell: ({ row }) => displayValue(row.original.receivedTotalUnits),
            },
            {
                id: 'discrepancies',
                header: 'Discrepancies',
                enableSorting: false,
                cell: ({ row }) => getDiscrepancySummary(row.original),
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: renderActionsCell,
            },
        ],
        [],
    );

    const tableMeta = useMemo(() => ({ onViewTransfer }), [onViewTransfer]);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row, index) => String(row.transferItemId ?? row.id ?? index),
        meta: tableMeta,
    });

    const loadedCount = tableData.length;
    const totalCount = totalCountProp ?? loadedCount;
    const showPagination = tableData.length > 0;

    return (
        <div className="flex flex-1 flex-col gap-2">
            {!isLoading && totalCount > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
                    <div className="shrink-0">
                        <ResultsCounter loadedCount={loadedCount} totalCount={totalCount} isRefreshing={isFetching} />
                    </div>
                </div>
            )}

            <div className="flex min-h-[320px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading FC transfer items…</span>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto pb-6">
                        <Table className="w-full min-w-[1400px] table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id} className="whitespace-normal break-words">
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table.getRowModel().rows.map((row) => <MemoizedRow key={row.id} row={row} />)
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {showPagination && (
                            <PaginationControls
                                hasPrevious={hasPrevious}
                                hasNext={hasNextPage}
                                onPrevious={onPrevious}
                                onNext={onNext}
                                isLoading={isFetching}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                onPageSizeChange={onPageSizeChange}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

MemoizedRow.displayName = 'MemoizedAllFCTransferItemsRow';
AllFCTransferItemsTable.displayName = 'AllFCTransferItemsTable';
